import classNames from "classnames";
import { parse } from "qs";
import { useEffect, useRef, useState } from "react";
import { useStore, useDispatch, useSelector } from "react-redux";
import { Redirect, Switch, useHistory, useLocation } from "react-router-dom";
import Route from "../Route";
import { checkWebView } from "../../actions/App/dispatchers";
import { fetch_audience } from "../../actions/DMP/dispatchers";
import { hydrate } from "../../reducers/SSR/reducer";
import { redirects } from "../../constants/redirects";
import routes from "../../core/entry/routes";
import { wallpaperObserver } from "../../utils/advertising/library";
import { concatSubjects } from "../../utils/dataTransform/ContentItem/library";
import PageActions from "../../utils/page/library";
import { checkAppVersion } from "../../utils/routing/library";
import Ad from "../Ad/component";
import AppStatus from "../AppStatus/component";
import DynamicHead from "../DynamicHead/component";
import ElectionHeader from "../Election/wrapper";
import ErrorBoundary from "../ErrorBoundary/component";
import Loader from "../Loader/component";
import MobileAppCta from "../MobileAppCta/component";
import PersistentPlayer from "../PersistentPlayer/component";
import UpsClient from "../UpsClient/component";
import {
    APP_STATUS_PATH,
    META_FOOTER_PATH,
    META_HEADER_PATH,
    TYPE_DETAIL,
} from "./constants";
import { useAdProps } from "./hooks";
import useViewport from "../../hooks/useViewport";
import log from "../../utils/logger/library";
import loadable from "@loadable/component";
import importMF from "../../federated/importMF";
import { ACC_MODULES } from "../../federated/accountCentre";
import AccessibilityNotification from "../AccessibilityNotification/component";
import ConductricsSurvey from "../ConductricsSurvey/component";
import { showPrivacyPreferences } from "../../utils/cmp/library";
import { fetch_all_newsletter_state } from "../../actions/Newsletters/actions";
import SuspensefulErrorBoundary from "../SuspensefulErrorBoundary/component";
import { createTrackedLazyComponent as trackLazy } from "../../core/chunk-tracker/component";

const GlobalHeader = trackLazy(
    () =>
        import(
            /* webpackChunkName: "GlobalHeader" */
            "../GlobalHeader/component"
        ),
    "GlobalHeader"
);
// eslint-disable-next-line import/no-unresolved
const GlobalFooter = loadable(() => import("../GlobalFooter/component"));

// Until the Client-side lifecycle takes over,
// the isomorphic server render takes
let serverRender = true;

// StrictMode escape hatch for Viafoura loading
// This follows the react official documentation:
// https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
let didInitViafoura = false;

const renderRedirects = Object.entries(redirects).map(([from, to], idx) => (
    <Redirect key={idx} from={from} to={to} exact />
));

let serverPath = "";

const selectLoading = (state) => {
    //get the path rendered on server
    if (serverRender) {
        serverPath = state.app.path;
        return false;
    }

    //make sure to not show the loader for the initially loaded page on the client
    if (serverPath === state.app.path) {
        return false;
    } else {
        //reset serverPath
        serverPath = "";
    }

    if (state.loader.loaded) return false;

    return true;
};

const AppContainer = () => {
    const store = useStore();
    const location = useLocation();
    const { listen } = useHistory();

    const [loading, setLoading] = useState(false);
    const appRef = useRef(null);
    const timer = useRef(null);
    const viewport = useViewport(appRef, "viewportLarge");
    const { adExclusions, contentId } = useAdProps(location);
    const dispatch = useDispatch();

    const ad_subject = useSelector((state) => state.detail.content.subject);
    const gs_categories = useSelector(
        (state) => state.detail.content.gs_categories
    );
    const gs_keywords = useSelector(
        (state) => state.detail.content.gs_keywords
    );
    const flag = useSelector((state) => state.detail.content.flag);
    const euCountry = useSelector((state) => state.gdpr.euCountry);
    const themeClassNames = useSelector((state) => state.app.themeClassNames);
    const pageType = useSelector((state) => state.app.pageType);
    const isLoading = useSelector(selectLoading);

    const renderRoutes = routes.map(({ path, component, exact }, index) => {
        const Component = component;
        return (
            <Route
                key={index}
                exact={exact}
                path={path}
                render={(renderProps) => {
                    const allProps = {
                        ...renderProps,
                        viewport: viewport,
                    };
                    return (
                        <SuspensefulErrorBoundary>
                            <Component {...allProps} />
                        </SuspensefulErrorBoundary>
                    );
                }}
            />
        );
    });

    // We are loading this script this way as Viafoura's entry point is too eager when placed in our html template
    // in ./core/html-templates/index. It will begin modifying markup before hydration has completed causing hydration
    // mismatch warnings. This method ensures that it will only be loaded after the page has hydrated.
    const loadViafouraAfterHydration = () => {
        if (!didInitViafoura) {
            didInitViafoura = true;
            const vfscript = document.createElement("script");
            vfscript.setAttribute("type", "text/javascript");
            vfscript.setAttribute(
                "src",
                "https://cdn.viafoura.net/entry/index.js"
            );
            vfscript.setAttribute("async", true);

            // Now that Viafoura needs a user interaction to initialize the library
            // we gaslight it into thinking that a user is interacting with the page
            // via the mousemove and touchmove events (for web & mobile browsers), after
            // the initial script loads. This prevents the indefinite Sign In button even
            // though the user might be logged in and is expecting the logged in state.
            vfscript.onload = () => {
                let intervalID = null;

                const ensureVFLoadsWithFakeUserInteraction = () => {
                    // handle web
                    const mouseEvent = new Event("mousemove");
                    // handle mobile
                    const touchEvent = new Event("touchmove");
                    touchEvent.changedTouches = touchEvent.changedTouches || []; // Required when SwiperJS is present
                    document.dispatchEvent(mouseEvent);
                    document.dispatchEvent(touchEvent);

                    // When the window.vf is loaded clear the interval
                    if (window.vf) {
                        clearInterval(intervalID);
                        intervalID = null;
                    }
                };

                intervalID = setInterval(
                    ensureVFLoadsWithFakeUserInteraction,
                    100
                );
            };

            document.body.appendChild(vfscript);
        }
    };

    useEffect(() => {
        PageActions.bindMediaQueries();
        const query = parse(location.search, {
            ignoreQueryPrefix: true,
        });

        // Check if the session is in an in-app browser
        if (query.webview) {
            try {
                dispatch(checkWebView(query.webview));
            } catch (error) {
                log.error("(AppContainer) Error setting isWebView", error);
            }
        }

        const displayPrivacyBanner =
            typeof query.displayPrivacyBanner === "string";

        if (displayPrivacyBanner) {
            // Ensure that Didomi is loaded before showing the privacy preferences
            window.CBCCMPQueue = window.CBCCMPQueue || [];
            window.CBCCMPQueue.push(() => {
                showPrivacyPreferences();
            });
        }

        try {
            dispatch(fetch_audience());
        } catch (error) {
            log.error("(AppContainer) Failed to fetch audience", error);
        }

        const unlistenToRouteChanges = listen(() => {
            checkAppVersion();
        });

        // Detect Wallpaper loaded
        wallpaperObserver();

        // Client-side takes over from the isomorphic server render
        serverRender = false;

        dispatch(hydrate());

        //load Viafoura
        loadViafouraAfterHydration();
        return () => {
            unlistenToRouteChanges();
        };
    }, []);

    useEffect(() => {
        if (isLoading) {
            timer.current = setTimeout(() => {
                setLoading(true);
            }, 1500);
        } else {
            clearTimeout(timer.current);
            if (loading === true) setLoading(false);
        }
    }, [isLoading]);

    useEffect(() => {
        return listen((history) => {
            if (history.hash !== "#content") {
                appRef.current.focus();
            }
        });
    }, []);

    useEffect(() => {
        // Init membership store from remote
        importMF(ACC_MODULES.initMembershipStore)
            .then((initMembershipStore) => {
                initMembershipStore(store)();
            })
            .catch((error) => {
                log.error(
                    `(AppContainer) Failed to initialize membership store [${error.message}]`
                );
            });
    }, []);

    useEffect(() => {
        dispatch(fetch_all_newsletter_state());
    }, []);

    // Determine whether to show the loading indicator
    const showIndicator = !serverRender && loading;
    const loadingClass = showIndicator ? "loading" : "loaded";
    const isDetail = pageType === TYPE_DETAIL;

    const appClasses = classNames(
        "appContainer",
        themeClassNames,
        viewport,
        loadingClass
    );

    const targeting = {
        type: "ocelot",
        contentId: contentId,
        subject: concatSubjects(ad_subject),
        flag,
        gs_categories,
        gs_keywords,
    };

    const currentPath = location.pathname.replace(/^\//, "");

    const renderGlobalHeader = (
        <SuspensefulErrorBoundary>
            <GlobalHeader
                isDetail={isDetail}
                viewport={viewport}
                route={{ path: currentPath, search: location.search }}
            />
        </SuspensefulErrorBoundary>
    );

    const isAccountPage = location.pathname.startsWith("/account/");

    const renderAd = !isAccountPage && (
        <div className="ad-risingstar-container">
            <Ad
                type="risingstar"
                targeting={Object.assign({ plc: "top" }, targeting)}
                exclusions={adExclusions}
                eu_country={euCountry}
            />
        </div>
    );

    const isHomepage = currentPath === "";

    const renderGlobalFooter = <GlobalFooter route={{ path: currentPath }} />;

    // In case we need to expose a component to be embedded on an external site,
    // (e.g. GlobalHeader or GlobalFooter) don't render them inside the Feed page template.
    if (currentPath.startsWith(META_HEADER_PATH)) {
        return renderGlobalHeader;
    }

    if (currentPath.startsWith(META_FOOTER_PATH)) {
        return renderGlobalFooter;
    }

    if (currentPath.startsWith(APP_STATUS_PATH)) {
        return <AppStatus />;
    }

    return (
        <div
            className={appClasses}
            ref={appRef}
            data-testid="app-container"
            data-cy="app-container"
        >
            <ErrorBoundary>
                <DynamicHead />
                <UpsClient />
                {showIndicator && <Loader />}

                <div className="full-width-container top-head">
                    {renderAd}
                    {renderGlobalHeader}
                    <AccessibilityNotification />
                </div>

                <div className="full-width-container mid-content">
                    {isHomepage && (
                        <Ad
                            type="wallpaper"
                            exclusions={adExclusions}
                            targeting={targeting}
                        />
                    )}
                    <ElectionHeader path={currentPath} />
                    <main className="feed-content content" id="content">
                        <div className="wallpaper-ad-wrap left">
                            <span className="ad" />
                        </div>
                        <div className="wallpaper-ad-wrap right">
                            <span className="ad" />
                        </div>

                        <Switch>
                            {renderRedirects}
                            {renderRoutes}
                        </Switch>
                    </main>
                </div>

                <div className="full-width-container bottom-foot">
                    {renderGlobalFooter}
                </div>

                {/* Below two components handle persistent video */}
                <ErrorBoundary>
                    <PersistentPlayer />
                </ErrorBoundary>
                <MobileAppCta />
                <ConductricsSurvey />
            </ErrorBoundary>
        </div>
    );
};

export default AppContainer;
